import { Button } from "@/components/Primitives/Button"
import { Card, CardContent, CardTitle } from "@/components/Primitives/Card"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { useConfig } from "@/hooks/useConfig"
import { Text } from "@/components/Primitives/Text"
import { useState } from "react"
import { PasswordRequirements } from "@/components/Primitives/PasswordRequirements/passwordRequirements"
import { Eye, EyeClosed } from "lucide-react"

interface ResetPasswordFormProps extends React.ComponentPropsWithoutRef<"div"> {
  password: string
  confirmPassword: string
  onPasswordChange: (password: string) => void
  onConfirmPasswordChange: (password: string) => void
  onSubmit: () => Promise<void>
  isSubmitting?: boolean
  error?: string
  passwordValidation?: boolean[]
  passwordRequirements?: { label: string }[]
  isPasswordValid?: boolean
}

export function ResetPasswordForm({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  isSubmitting,
  error,
  passwordValidation = [],
  passwordRequirements = [],
  isPasswordValid = true,
}: ResetPasswordFormProps) {
  const { t } = useConfig()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  return (
    <Card>
      <CardTitle>{t("config_reset_password_description")}</CardTitle>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
        >
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="password" className="font-semibold">
                {t("config_reset_password_new_label")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="password"
                  placeholder={t("config_reset_password_new_placeholder")}
                  value={password}
                  required
                  onChange={(event) => {
                    onPasswordChange(event.target.value)
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeClosed className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <PasswordRequirements
                password={password}
                validation={passwordValidation}
                requirements={passwordRequirements}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword" className="font-semibold">
                {t("config_reset_password_confirm_label")}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  data-testid="confirmPassword"
                  placeholder={t("config_reset_password_confirm_placeholder")}
                  value={confirmPassword}
                  required
                  onChange={(event) => {
                    onConfirmPasswordChange(event.target.value)
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-2"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? (
                    <EyeClosed className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full mt-6"
            data-testid="submit"
            disabled={isSubmitting || !isPasswordValid}
          >
            {isSubmitting
              ? t("config_reset_password_button_submitting")
              : t("config_reset_password_button_continue")}
          </Button>
          {error && (
            <Text variant="small" className="text-red-500 mt-2 text-center">
              {error}
            </Text>
          )}
        </form>
      </CardContent>
    </Card>
  )
}
